import React, { useState, useEffect, useRef } from "react";
import {
  FiBookOpen,
  FiCheck,
  FiChevronDown,
  FiDownload,
  FiEdit2,
  FiFileText,
  FiMessageSquare,
  FiSearch,
  FiSettings,
  FiActivity,
  FiSend,
  FiSliders,
} from "react-icons/fi";
import { FaSpinner } from "react-icons/fa";
import { FiTrash } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { getChatResponseData } from "./services/chatResponse.service";
import { IMessage } from "./interfaces/message";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import styles from "./ChatAI.module.css";
import Markdown from "react-markdown";
import { IOutputAdjustment } from "../../../../../DetailsPage/components/SummaryInfoBlock/components/ChatAI/interfaces/OutputAdjustment";
import { useMutation } from "@tanstack/react-query";
import { IoCopy } from "react-icons/io5";
import { Tooltip } from "react-tooltip";
import { useFilterContext } from "../../../../../../context/FilterContext";
import { PromptBlock } from "./components/PromptBlock/PromptBlock";
import { getTodayDocxFilename } from "../../../../helpers/getTodayDocxFilename";
import { exportChatConv } from "./services/exportChatConv.service";
import {
  createMemoJob,
  waitForMemoJob,
  downloadMemoJob,
} from "../../../../../DetailsPage/services/generateMemos.service";
import { formatDate } from "../../../../helpers/formatDate";
import { ModalOutputAdjustments } from "./components/ModalOutputAdjustments/ModalOutputAdjustments";
import { toast } from "react-toastify";
import { TbRotateClockwise } from "react-icons/tb";
import handleCopyToClipboard from "../../../../../../helpers/handleCopyToClipboard";
import { useDefaultFiltersContext } from "../../../../../../context/DefaultFiltersContext";
import { MarkdownEditor } from "../../../../../DetailsPage/components/SummaryInfoBlock/components/MarkdownEditor/MarkdownEditor";
import { ChatLimitBanner } from "../../../../../../components/ChatLimitBanner/ChatLimitBanner";
import {
  ChatLimitReachedError,
  useChatRequestsLeft,
} from "../../../../../../helpers/chatRequestsLeft";

interface IChatAIProps {
  selectedDocuments: { code: string; case_no: string }[];
  setSelectedDocuments: (usedIds: { code: string; case_no: string }[]) => void;
}

const ChatAI: React.FC<IChatAIProps> = ({
  selectedDocuments,
  setSelectedDocuments,
}) => {
  const { t } = useTranslation();
  const [valueConversationInput, setValueConversationInput] =
    useState<string>("");
  const [waitingMessage, setWaitingMessage] = useState("Loading...");
  const [waitingSteps, setWaitingSteps] = useState<string[]>([]);
  const [valueInput, setValueInput] = useState<string>("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(true);
  const [outputAdj, setOutputAdj] = useState<IOutputAdjustment>({
    tone: null,
    format: null,
    length: null,
  });
  const [settingsFilter, setSettingsFilter] = useState<{
    country: string[];
    regulator: string[];
    classes: string[];
  }>({ country: [], regulator: [], classes: [] });

  const { filterState } = useFilterContext();
  const { defaultFilters } = useDefaultFiltersContext();
  const isLimitReached = useChatRequestsLeft() === 0;

  const isWaitingPhase = useRef(true);
  const streamingBuffer = useRef("");
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const lastUserMsgRef = useRef<HTMLDivElement | null>(
    null,
  ) as React.MutableRefObject<HTMLDivElement | null>;

  const [scrollToLastUser, setScrollToLastUser] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isGeneratingMemoIndex, setIsGeneratingMemoIndex] = useState<
    number | null
  >(null);

  const landingInputRef = useRef<HTMLTextAreaElement>(null);
  const convInputRef = useRef<HTMLTextAreaElement>(null);
  const markdownRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const copyTimeouts = useRef<Record<number, number>>({});

  const [copiedToBuffer, setCopiedToBuffer] = useState<{
    [key: number]: boolean;
  }>({});
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState("");

  const MAX_INPUT_HEIGHT = 288;

  const autoResize = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "auto";
    const nextHeight = Math.min(el.scrollHeight, MAX_INPUT_HEIGHT);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > MAX_INPUT_HEIGHT ? "auto" : "hidden";
  };

  const stripStepEmoji = (label: string) =>
    label.replace(/^[^A-Za-z0-9]+\s*/, "");

  const lastUserMsgIdx = messages.map((m) => m.role).lastIndexOf("user");
  const lastAssistantMsgIdx = messages
    .map((m) => m.role)
    .lastIndexOf("assistant");

  const hasChatHistory = messages.some(
    (m) => m.role === "user" || (m.role === "assistant" && m.text.trim() !== ""),
  );

  const handleCopyClick = async (index: number) => {
    await handleCopyToClipboard({ current: markdownRefs.current[index] });
    setCopiedToBuffer((prev) => ({ ...prev, [index]: true }));
    if (copyTimeouts.current[index]) {
      window.clearTimeout(copyTimeouts.current[index]);
    }
    copyTimeouts.current[index] = window.setTimeout(() => {
      setCopiedToBuffer((prev) => ({ ...prev, [index]: false }));
    }, 3000);
  };

  const openEditPanel = (index: number) => {
    const currentText = messages[index]?.text ?? "";
    setEditIndex(index);
    setEditDraft(currentText);
    setIsEditOpen(true);
  };

  const closeEditPanel = () => {
    setIsEditOpen(false);
    setEditIndex(null);
    setEditDraft("");
  };

  const saveEdit = async () => {
    if (editIndex === null) return;
    const nextText = editDraft.trim();
    if (!nextText) return;

    setMessages((prev) => {
      const updated = [...prev];
      if (updated[editIndex] && updated[editIndex].role === "assistant") {
        updated[editIndex] = { ...updated[editIndex], text: nextText };
      }
      return updated;
    });

    closeEditPanel();
  };

  const getStepIcon = (label: string) => {
    const normalized = stripStepEmoji(label).toLowerCase();
    const iconClass =
      "w-4 h-4 flex-shrink-0 self-start text-slate-500 dark:text-slate-400";

    if (normalized.startsWith("analyse")) {
      return <FiActivity className={iconClass} />;
    }
    if (
      normalized.startsWith("collect") ||
      normalized.includes("search query") ||
      normalized.startsWith("iteration")
    ) {
      return <FiSearch className={iconClass} />;
    }
    if (
      normalized.startsWith("retrieve") ||
      normalized.startsWith("extracting") ||
      normalized.startsWith("agent started")
    ) {
      return <FiFileText className={iconClass} />;
    }
    if (
      normalized.startsWith("generate") ||
      normalized.startsWith("writing")
    ) {
      return <FiSettings className={iconClass} />;
    }
    if (normalized.startsWith("answer")) {
      return <FiMessageSquare className={iconClass} />;
    }

    return <FiFileText className={iconClass} />;
  };

  const scrollChatToBottom = () => {
    const el = chatContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  const handleChatScroll = () => {
    const el = chatContainerRef.current;
    if (!el) return;
    const threshold = 24;
    const atBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
    setShowScrollToBottom(!atBottom);
  };

  useEffect(() => {
    handleChatScroll();
  }, [messages.length, isSubmitted]);

  const readSettingsFromLS = () => {
    try {
      const raw = localStorage.getItem("settings");
      if (!raw) return { country: [], regulator: [], classes: [] };
      const parsed = JSON.parse(raw);
      return {
        country: Array.isArray(parsed?.settings?.country)
          ? parsed.settings.country
          : Array.isArray(parsed?.country)
            ? parsed.country
            : [],
        regulator: Array.isArray(parsed?.settings?.regulator)
          ? parsed.settings.regulator
          : Array.isArray(parsed?.regulator)
            ? parsed.regulator
            : [],
        classes: Array.isArray(parsed?.settings?.classes)
          ? parsed.settings.classes
          : Array.isArray(parsed?.classes)
            ? parsed.classes
            : [],
      };
    } catch {
      return { country: [], regulator: [], classes: [] };
    }
  };

  useEffect(() => {
    const handleSettingsUpdated = () => {
      setSettingsFilter(readSettingsFromLS());
    };

    handleSettingsUpdated();
    window.addEventListener(
      "settings:updated",
      handleSettingsUpdated as EventListener,
    );
    window.addEventListener("storage", handleSettingsUpdated);

    return () => {
      window.removeEventListener(
        "settings:updated",
        handleSettingsUpdated as EventListener,
      );
      window.removeEventListener("storage", handleSettingsUpdated);
    };
  }, []);


  const applySettingsToFilters = (filters: typeof filterState) => {
    const next = { ...filters };

    if (!filters.country?.length && settingsFilter.country.length > 0) {
      next.country = settingsFilter.country;
    }

    if (!filters.regulator?.length) {
      if (settingsFilter.regulator.length > 0) {
        next.regulator = settingsFilter.regulator;
      }
    }

    if (!filters.classes?.length && settingsFilter.classes.length > 0) {
      next.classes = settingsFilter.classes;
    }

    return next;
  };

  const effectiveFilters = applySettingsToFilters(filterState);

  const {
    mutate: fetchChatResponse,
    isPending,
    isError,
  } = useMutation({
    mutationFn: async (inputMessages: IMessage[]) =>
      await getChatResponseData(
        selectedDocuments.length > 0 ? selectedDocuments : [],
        {
          ...effectiveFilters,
          date_from: formatDate(effectiveFilters.date_from),
          date_to: formatDate(effectiveFilters.date_to),
          date_issue_from: formatDate(effectiveFilters.date_issue_from),
          date_issue_to: formatDate(effectiveFilters.date_issue_to),
        },
        inputMessages,
        outputAdj,
        "search",
        handleStreamResponse,
        onFullResponse,
      ),
    onError: (error) => {
      console.error("Error fetching chat response:", error);
      if (!(error instanceof ChatLimitReachedError)) {
        toast.error(t("errorChatAIResponse"));
      }
      setMessages((prevMessages) => {
        const updated = [...prevMessages];
        const lastMsg = updated[updated.length - 1];
        if (lastMsg && lastMsg.role === "assistant" && lastMsg.text === "") {
          updated.pop();
        }
        return updated;
      });
    },
  });

  const isBusy = isPending || isGeneratingMemoIndex !== null;

  const { mutate: exportChat, isPending: isExportingChat } = useMutation({
    mutationFn: async () => await exportChatConv(messages),
    onSuccess: (data: ArrayBuffer) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = getTodayDocxFilename();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    onError: (error) => {
      console.error("Error exporting chat conversation:", error);
    },
  });

  const onFullResponse = (fullResponse: string) => {
    const lastClearIndex = fullResponse.lastIndexOf("--CLEAR--");

    let responsePart = fullResponse.replace(/--KEEP-ALIVE--/g, "");
    if (lastClearIndex !== -1) {
      responsePart = responsePart.substring(
        lastClearIndex + "--CLEAR--".length,
      );
    }

    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];

      const lastAssistantIndex = [...updatedMessages]
        .reverse()
        .findIndex((m) => m.role === "assistant");

      if (lastAssistantIndex !== -1) {
        const realIndex = updatedMessages.length - 1 - lastAssistantIndex;
        updatedMessages[realIndex] = {
          role: "assistant",
          text: responsePart,
        };
        return updatedMessages;
      }

      return [...updatedMessages, { role: "assistant", text: responsePart }];
    });
  };

  const handleStreamResponse = (chunk: string) => {
    try {
      const cleaned = chunk.replace(/--KEEP-ALIVE--/g, "");
      const segments = cleaned.split("--CLEAR--");

      const progressEmojis = ["🧠", "📑", "🔎", "📚", "⚙️", "🔁", "🔍", "📝"];
      const progressKeywords = [
        "analyse",
        "analy",
        "retrieve",
        "collect",
        "search query",
        "iteration",
        "extract",
        "generate",
        "writing",
        "agent started",
        "answer",
      ];

      const isProgressSegment = (p: string): boolean => {
        if (!p) return false;
        const trimmed = p.trim();
        if (!trimmed) return false;
        if (progressEmojis.some((e) => trimmed.startsWith(e))) return true;
        const low = trimmed.toLowerCase();
        return progressKeywords.some((k) => low.includes(k));
      };

      segments.forEach((seg) => {
        const part = (seg || "").trim();
        if (!part) return;

        if (/--RESPONSE--/i.test(part)) {
          const after = part.replace(/--RESPONSE--/i, "").trim();

          streamingBuffer.current += after;

          setMessages((prevMessages) => {
            const updated = [...prevMessages];
            const lastAssistantIndex = [...updated]
              .reverse()
              .findIndex((m) => m.role === "assistant");

            const finalText = streamingBuffer.current || "";

            if (lastAssistantIndex !== -1) {
              const realIndex = updated.length - 1 - lastAssistantIndex;
              updated[realIndex] = { role: "assistant", text: finalText };
            } else {
              updated.push({ role: "assistant", text: finalText });
            }

            return updated;
          });

          streamingBuffer.current = "";
          isWaitingPhase.current = false;
          setWaitingMessage("");
          setWaitingSteps([]);
          return;
        }

        if (isProgressSegment(part)) {
          isWaitingPhase.current = true;
          const normalizedPart = part.replace(/\s+/g, " ").trim();
          if (!normalizedPart) return;
          setWaitingMessage(normalizedPart);
          setWaitingSteps((prev) =>
            prev[prev.length - 1] === normalizedPart
              ? prev
              : [...prev, normalizedPart],
          );
          return;
        }

        streamingBuffer.current += part;
      });
    } catch (err) {
      console.error("stream handling error:", err);
    }
  };

  const handleSubmit = (val: string, isRetry = false) => {
    if (val.trim() && !isLimitReached) {
      setValueConversationInput("");
      setIsSubmitted(true);

      if (!isRetry) {
        setMessages((prevMessages) => [
          ...prevMessages,
          { role: "user", text: val },
          { role: "assistant", text: "" },
        ]);
      } else {
        setMessages((prevMessages) => {
          const updated = [...prevMessages];
          const last = updated[updated.length - 1];
          if (!(last && last.role === "assistant" && last.text === "")) {
            updated.push({ role: "assistant", text: "" });
          }
          return updated;
        });
      }

      setWaitingMessage("");
      isWaitingPhase.current = true;
      setWaitingSteps([]);

      const lastMessage = messages[messages.length - 1];
      const isDuplicate =
        lastMessage?.role === "user" && lastMessage?.text === val;

      fetchChatResponse(
        isDuplicate ? messages : [...messages, { role: "user", text: val }],
      );
      setScrollToLastUser(true);

      if (convInputRef.current) {
        convInputRef.current.style.height = "auto";
        convInputRef.current.rows = 1;
      }
    }
  };

  useEffect(() => {
    if (
      scrollToLastUser &&
      lastUserMsgRef.current &&
      chatContainerRef.current
    ) {
      setTimeout(() => {
        chatContainerRef.current!.scrollTop =
          lastUserMsgRef.current!.offsetTop -
          chatContainerRef.current!.offsetTop;
        setScrollToLastUser(false);
      }, 0);
    }
  }, [scrollToLastUser, messages]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    autoResize(e.target);
    setValueInput(val);
  };

  const handleParsePrompt = (val: string) => {
    if (isSubmitted) {
      setValueConversationInput(val);
      setValueInput("");
      requestAnimationFrame(() => autoResize(convInputRef.current));
    } else {
      setValueInput(val);
      setValueConversationInput("");
      requestAnimationFrame(() => autoResize(landingInputRef.current));
    }

    setIsPanelCollapsed(true);
    setIsModalOpen(false);
  };

  const downloadConv = () => {
    exportChat();
  };

  useEffect(() => {
    if (!isPanelCollapsed) {
      setIsModalOpen(false);
    }
  }, [isPanelCollapsed]);

  const handleConvInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    autoResize(e.target);
    setValueConversationInput(val);
  };

  useEffect(() => {
    const backup = localStorage.getItem("search_chatAI_backup");
    if (backup) {
      try {
        const parsed = JSON.parse(backup);
        if (parsed?.messages?.length > 0) {
          setMessages(parsed.messages);
          setSelectedDocuments(parsed.usedIds);
          setIsSubmitted(true);

          if (parsed.outputAdj) setOutputAdj(parsed.outputAdj);

          const lastUserMsg = [...parsed.messages]
            .reverse()
            .find((m) => m.role === "user")?.text;
          if (lastUserMsg) {
            setTimeout(() => {
              fetchChatResponse(parsed.messages);
            }, 3000);
          }
        }
      } catch (e) {
        console.error("Failed to restore chat after relogin:", e);
      } finally {
        localStorage.removeItem("search_chatAI_backup");
      }
    }
  }, []);

  const handleKeyDown =
    (valueGetter: () => string) =>
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();

        const value = valueGetter();
        if (value.trim()) {
          handleSubmit(value);
        }
      }
    };

  return (
    <div className="bg-white dark:bg-slate-900/60 text-secondary-color flex flex-col h-full min-h-0 relative rounded-xl">
      <div className="w-full transition-all duration-300 flex flex-col border-gray-300 flex-grow min-h-0">
        {!isSubmitted ? (
          <div className="flex flex-col flex-grow min-h-0 bg-white dark:bg-slate-900 text-gray-600 dark:text-white rounded-xl">
            <div
              className={`w-full flex flex-col items-center ${
                isPanelCollapsed ? "flex-1 justify-center" : "pt-16 pb-4"
              }`}
            >
              <h1 className="text-xl font-semibold mb-10 text-center">
                {t("chatAI.howMayIHelp")}
              </h1>

              <div className="w-3/4 mb-3">
                <ChatLimitBanner />
              </div>

              <div className="w-3/4 mb-4 flex items-center gap-2">
                <div className="flex-1 relative">
                  <textarea
                    ref={landingInputRef}
                    disabled={isLimitReached}
                    placeholder={t("chatAI.askLegalQuestion")}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white/90 dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 resize-none overflow-y-hidden min-h-[40px] text-sm leading-5 px-4 py-3 pr-14 focus:outline-none focus:ring-2 focus:ring-del-green"
                    value={valueInput}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown(() => valueInput)}
                    rows={1}
                  />
                  {valueInput.trim() && !isLimitReached && (
                    <button
                      className="absolute right-2 bottom-2 h-9 w-9 flex items-center justify-center rounded-full text-del-green hover:text-del-light-theme-green"
                      onClick={() => handleSubmit(valueInput)}
                    >
                      <FiSend />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2 self-center -translate-y-0.5">
                  <button
                    className={`tooltip-open-outputAdj h-10 w-10 flex items-center justify-center rounded-lg border ${
                      isModalOpen
                        ? "border-del-green bg-del-green/10 text-del-light-theme-green dark:bg-del-green/20"
                        : "border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-800 text-slate-600 dark:text-slate-200 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                    }`}
                    onClick={() => setIsModalOpen(!isModalOpen)}
                  >
                    <FiSliders />
                  </button>

                  <button
                    className={`tooltip-promptlib h-10 w-10 flex items-center justify-center rounded-lg border ${
                      !isPanelCollapsed
                        ? "border-del-green bg-del-green/10 text-del-light-theme-green dark:bg-del-green/20"
                        : "border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-800 text-slate-600 dark:text-slate-200 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                    }`}
                    onClick={() => setIsPanelCollapsed((prev) => !prev)}
                  >
                    <FiBookOpen />
                  </button>

                  {isModalOpen && (
                    <ModalOutputAdjustments
                      onClose={() => setIsModalOpen(false)}
                      isSubmit={false}
                      onOutputAdjChange={(outputs: IOutputAdjustment) =>
                        setOutputAdj(outputs)
                      }
                      outputAdj={outputAdj}
                    />
                  )}
                </div>
              </div>
            </div>

            <Tooltip
              anchorSelect=".tooltip-promptlib"
              place="top"
              className="text-xs font-light z-50"
              content={
                isPanelCollapsed
                  ? t("chatAI.openPromptLibraryTooltip")
                  : t("chatAI.closePromptLibraryTooltip")
              }
            />

            <Tooltip
              anchorSelect=".tooltip-open-outputAdj"
              place="top"
              className="text-xs font-light z-50"
              content={t("chatAI.openOutputAdjTooltip")}
            />

            {!isPanelCollapsed && (
              <div className="w-full flex-1 min-h-0 flex justify-center pb-4">
                <div className="w-3/4 min-h-0 overflow-hidden">
                  <PromptBlock
                    isPanelCollapsed={isPanelCollapsed}
                    setIsPanelCollapsed={setIsPanelCollapsed}
                    handleSubmit={handleParsePrompt}
                    isSubmitted={false}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex-1 min-h-0 relative flex flex-col">
              <div
                className="flex-1 min-h-0 overflow-y-auto space-y-3 pt-4 pb-2 px-4"
                ref={chatContainerRef}
                onScroll={handleChatScroll}
              >
                {messages.map((message, index) => (
                  <React.Fragment key={`mainpage-chat-fragment-${index}`}>
                    {message.text === "" && isPending ? (
                      <div className="w-full flex justify-start">
                        <div className="max-w-[520px] w-full rounded-xl bg-white/80 dark:bg-slate-800/70 px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {waitingSteps.length === 0 ? (
                            <div className="flex items-center gap-2">
                              <FaSpinner className="animate-spin text-xs" />
                            </div>
                          ) : (
                            <div className="relative flex flex-col gap-0">
                              <div className="absolute left-2 top-2 bottom-2 w-px bg-slate-300 dark:bg-slate-600 z-0" />
                              {waitingSteps.map((step, idx) => {
                                const activeIdx = waitingSteps.length - 1;
                                const isActive = idx === activeIdx;
                                const isDone = idx < activeIdx;

                                return (
                                  <div
                                    key={`waiting-step-${idx}`}
                                    className="relative"
                                  >
                                    <div
                                      className={[
                                        "flex items-start gap-2 py-1.5",
                                        isActive
                                          ? "text-slate-700 dark:text-slate-100 font-medium"
                                          : isDone
                                            ? "text-slate-400 dark:text-slate-500"
                                            : "text-slate-500 dark:text-slate-400",
                                      ].join(" ")}
                                    >
                                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 relative z-10 bg-white/80 dark:bg-slate-800/70">
                                        {isDone ? (
                                          <span className="w-2 h-2 rounded-full bg-slate-400 dark:bg-slate-500" />
                                        ) : isActive ? (
                                          <FaSpinner className="animate-spin text-xs" />
                                        ) : (
                                          ""
                                        )}
                                      </span>
                                      <span className="flex items-start gap-2 min-w-0">
                                        {getStepIcon(step)}
                                        <span>{stripStepEmoji(step)}</span>
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`w-full flex ${
                          message.role === "user"
                            ? "justify-end"
                            : "justify-start"
                        }`}
                      >
                        <div className="inline-flex flex-col items-start">
                          <div
                            ref={(el) => {
                              markdownRefs.current[index] = el;

                              if (index === lastUserMsgIdx) {
                                lastUserMsgRef.current = el;
                              }
                            }}
                            className={`relative inline-block text-left break-words text-gray-800 dark:text-white ${
                              message.role === "user"
                                ? "whitespace-pre-wrap max-w-max p-3 bg-del-green/20 dark:bg-del-green/25 rounded-xl rounded-br-none border border-slate-200 dark:border-slate-700 self-end ml-auto"
                                : "max-w-max p-0"
                            }`}
                          >
                            {message.role === "assistant" &&
                            isEditOpen &&
                            editIndex === index ? (
                              <div className="w-full max-w-[900px]">
                                <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2">
                                  <MarkdownEditor
                                    data={editDraft}
                                    onChange={setEditDraft}
                                    className="mdx-chat-editor min-h-[280px] px-3 py-2"
                                  />
                                </div>
                                <div className="mt-3 flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={saveEdit}
                                    className="px-2 py-1 text-xs font-medium text-white bg-del-green rounded-md hover:bg-del-light-theme-green"
                                  >
                                    {t("chatAI.saveEdit", {
                                      defaultValue: "Save",
                                    })}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={closeEditPanel}
                                    className="px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700"
                                  >
                                    {t("chatAI.cancelEdit", {
                                      defaultValue: "Cancel",
                                    })}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <Markdown
                                components={{
                                  a: ({ node, ...props }) => (
                                    <a
                                      target="_blank"
                                      className="text-blue-500 dark:text-blue-300"
                                      {...props}
                                    />
                                  ),
                                }}
                                rehypePlugins={[rehypeRaw]}
                                remarkPlugins={[remarkGfm, remarkBreaks]}
                                className={
                                  message.role === "user"
                                    ? styles.markdownBlock + " flex-grow"
                                    : styles.markdownChatAIBlock + " flex-grow"
                                }
                              >
                                {message.text}
                              </Markdown>
                            )}
                          </div>

                          {message.role === "assistant" &&
                            message.text.trim() !== "" &&
                            !isWaitingPhase.current &&
                            (!isPending || index !== lastAssistantMsgIdx) &&
                            !(isEditOpen && editIndex === index) && (
                              <div className="mt-1 self-end flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => openEditPanel(index)}
                                  className={`tooltip-edit-${index} text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors focus:outline-none`}
                                >
                                  <FiEdit2 size={18} />
                                </button>
                                <Tooltip
                                  anchorSelect={`.tooltip-edit-${index}`}
                                  place="top"
                                  positionStrategy="fixed"
                                  closeOnScroll
                                  className="text-xs font-light z-50"
                                >
                                  {t("chatAI.edit", { defaultValue: "Edit" })}
                                </Tooltip>

                                {(() => {
                                  const isPolandSelected = () => {
                                    const regs: string[] =
                                      effectiveFilters?.regulator || [];
                                    const countries: string[] =
                                      effectiveFilters?.country || [];
                                    if (
                                      countries.some((c) =>
                                        c.toLowerCase().includes("poland"),
                                      )
                                    )
                                      return true;
                                    if (
                                      regs.some(
                                        (r) =>
                                          r &&
                                          r.toLowerCase().includes("poland"),
                                      )
                                    )
                                      return true;
                                    if (
                                      selectedDocuments?.some(
                                        (d) =>
                                          d.code &&
                                          d.code.toLowerCase().startsWith("pl"),
                                      )
                                    )
                                      return true;
                                    return false;
                                  };

                                  if (!isPolandSelected()) return null;

                                  return (
                                    <>
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          try {
                                            setIsGeneratingMemoIndex(index);

                                            const created = await createMemoJob(
                                              message.text,
                                            );
                                            toast.info("Memo generation started. Please wait...");



                                            const finalJob = await waitForMemoJob(
                                              created.job_id,
                                              (job) => {
                                                if (job.status === "pending") {
                                                  // queued
                                                } else if (job.status === "running") {
                                                }
                                              },
                                              2000,
                                              20 * 60 * 1000,
                                            );

                                            if (finalJob.status === "done") {
                                              try {
                                                await downloadMemoJob(created.job_id);
                                                toast.success("Memos are ready");
                                              } catch (e) {
                                                throw e;
                                              }
                                            }
                                            } catch (err: any) {
                                              console.error("generateMemos error:", err);
                                            let message = "Failed to generate memos";
                                            const resp = err?.response?.data;
                                            if (resp) {
                                              if (typeof resp === "string") message = resp;
                                              else if (resp.error) message = resp.error;
                                              else if (resp.detail) message = resp.detail;
                                              else if (resp.error_message) message = resp.error_message;
                                            } else if (err?.message) {
                                              message = err.message;
                                            }
                                            toast.error(message);
                                          } finally {
                                            setIsGeneratingMemoIndex(null);
                                          }
                                        }}
                                        className={`tooltip-gen-memo-${index} text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors focus:outline-none`}
                                      >
                                        {isGeneratingMemoIndex === index ? (
                                          <FaSpinner className="animate-spin" />
                                        ) : (
                                          <FiFileText size={18} />
                                        )}
                                      </button>
                                      <Tooltip
                                        anchorSelect={`.tooltip-gen-memo-${index}`}
                                        place="top"
                                        positionStrategy="fixed"
                                        closeOnScroll
                                        className="text-xs font-light z-50"
                                      >
                                        {t("chatAI.generateMemo", {
                                          defaultValue: "Generate memo",
                                        })}
                                      </Tooltip>
                                    </>
                                  );
                                })()}

                                <button
                                  type="button"
                                  onClick={() => handleCopyClick(index)}
                                  className={`tooltip-context-${index} text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors focus:outline-none`}
                                >
                                  {copiedToBuffer[index] ? (
                                    <FiCheck size={18} />
                                  ) : (
                                    <IoCopy size={18} />
                                  )}
                                </button>
                                <Tooltip
                                  anchorSelect={`.tooltip-context-${index}`}
                                  place="top"
                                  positionStrategy="fixed"
                                  closeOnScroll
                                  className="text-xs font-light z-50"
                                >
                                  {copiedToBuffer[index]
                                    ? t("chatAI.copied")
                                    : t("chatAI.copyText")}
                                </Tooltip>
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}

                {isError && messages.length > 0 && !isLimitReached && (
                  <div className="flex flex-col items-center justify-center text-center text-gray-500 dark:text-gray-400">
                    <TbRotateClockwise
                      className="text-4xl cursor-pointer hover:text-del-green transition-colors"
                      onClick={() => {
                        const lastUserMsg = [...messages]
                          .reverse()
                          .find((m) => m.role === "user")?.text;

                        if (lastUserMsg) {
                          handleSubmit(lastUserMsg, true);
                        }
                      }}
                    />
                    <p className="mt-2 text-sm">{t("retryMessage")}</p>
                  </div>
                )}
              </div>

              {showScrollToBottom && (
                <button
                  className="tooltip-scroll-bottom absolute bottom-3 right-3 h-9 w-9 flex items-center justify-center rounded-full border border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-800 text-slate-700 dark:text-slate-200 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                  onClick={scrollChatToBottom}
                >
                  <FiChevronDown />
                </button>
              )}
            </div>

            {isSubmitted && !isPanelCollapsed && (
              <div className="px-3 pb-2 flex-shrink-0">
                <div
                  className={`min-h-0 ${
                    hasChatHistory ? "h-[420px] max-h-[48vh]" : "h-[320px]"
                  }`}
                >
                  <PromptBlock
                    isPanelCollapsed={isPanelCollapsed}
                    setIsPanelCollapsed={setIsPanelCollapsed}
                    handleSubmit={handleParsePrompt}
                    isSubmitted={isSubmitted}
                  />
                </div>
              </div>
            )}

            <div className="px-3 py-2 flex-shrink-0">
              <div className="mb-2">
                <ChatLimitBanner />
              </div>
              <div className="relative flex items-end gap-2 pr-[180px]">
                <div className="flex items-center gap-2 flex-grow">
                  <div className="flex-1 relative">
                    <textarea
                      ref={convInputRef}
                      disabled={isLimitReached}
                      placeholder={t("chatAI.askLegalQuestion")}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 resize-none overflow-y-hidden min-h-[40px] text-sm leading-5 px-4 py-3 pr-14 focus:outline-none focus:ring-2 focus:ring-del-green"
                      value={valueConversationInput}
                      onChange={handleConvInputChange}
                      onKeyDown={handleKeyDown(() => valueConversationInput)}
                      rows={1}
                    />
                    {!!valueConversationInput.trim() && !isLimitReached && (
                      <button
                        className="absolute right-2 bottom-2 h-9 w-9 flex items-center justify-center rounded-full text-del-green hover:text-del-light-theme-green"
                        onClick={() => handleSubmit(valueConversationInput)}
                        disabled={isPending}
                      >
                        <FiSend />
                      </button>
                    )}
                  </div>
                </div>

                <div className="absolute right-0 bottom-2 flex items-center gap-2">
                  <button
                    className={`tooltip-open-outputAdj h-9 w-9 flex items-center justify-center rounded-lg border ${
                      isModalOpen
                        ? "border-del-green bg-del-green/10 text-del-light-theme-green dark:bg-del-green/20"
                        : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                    }`}
                    onClick={() => setIsModalOpen(!isModalOpen)}
                  >
                    <FiSliders />
                  </button>

                  <button
                    className={`tooltip-promptlib h-9 w-9 flex items-center justify-center rounded-lg border ${
                      !isPanelCollapsed
                        ? "border-del-green bg-del-green/10 text-del-light-theme-green dark:bg-del-green/20"
                        : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                    }`}
                    onClick={() => setIsPanelCollapsed((prev) => !prev)}
                  >
                    <FiBookOpen />
                  </button>

                  <button
                    className={`tooltip-clear-chatai-conv h-9 w-9 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-red-600 dark:text-red-400 ${
                      isBusy
                        ? "cursor-not-allowed opacity-50"
                        : "hover:bg-red-50 dark:hover:bg-red-500/10"
                    }`}
                    onClick={() => {
                      if (!isBusy) setMessages([]);
                    }}
                    disabled={isBusy}
                  >
                    <FiTrash />
                  </button>

                  <button
                    className={`${
                      messages.length === 0 || isExportingChat || isPending
                        ? "cursor-not-allowed opacity-50"
                        : "tooltip-export-chatai-conv hover:bg-del-green dark:hover:bg-del-green hover:text-white"
                    } h-9 w-9 flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200`}
                    onClick={() => {
                      if (!isPending && !isExportingChat) downloadConv();
                    }}
                    disabled={
                      messages.length === 0 || isExportingChat || isPending
                    }
                  >
                    {isExportingChat ? (
                      <FaSpinner className="animate-spin" size={14} />
                    ) : (
                      <FiDownload />
                    )}
                  </button>
                </div>
              </div>

              <Tooltip
                anchorSelect=".tooltip-export-chatai-conv"
                place="top"
                className="text-xs font-light z-50"
                content={t("chatAI.exportChatConvTooltip")}
              />
              <Tooltip
                anchorSelect=".tooltip-promptlib"
                place="top"
                className="text-xs font-light z-50"
                content={
                  isPanelCollapsed
                    ? t("chatAI.openPromptLibraryTooltip")
                    : t("chatAI.closePromptLibraryTooltip")
                }
              />
              <Tooltip
                anchorSelect=".tooltip-clear-chatai-conv"
                place="top"
                className="text-xs font-light z-50"
                content={t("chatAI.clearChatConvTooltip")}
              />
              <Tooltip
                anchorSelect=".tooltip-scroll-bottom"
                place="top"
                className="text-xs font-light z-50"
                content={t("chatAI.scrollToBottom", {
                  defaultValue: "Scroll to bottom",
                })}
              />
              <Tooltip
                anchorSelect=".tooltip-open-outputAdj"
                place="top"
                className="text-xs font-light z-50"
                content={t("chatAI.openOutputAdjTooltip")}
              />

              {isModalOpen && (
                <ModalOutputAdjustments
                  onClose={() => setIsModalOpen(false)}
                  isSubmit={true}
                  onOutputAdjChange={(outputs: IOutputAdjustment) =>
                    setOutputAdj(outputs)
                  }
                  outputAdj={outputAdj}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export { ChatAI };